import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export class CourseCompletionWatcher {
  private static listeners = new Map<string, () => void>();
  
  static watchCourseProgress(userId: string, courseId: string, onComplete: () => void) {
    const key = `${userId}-${courseId}`;
    
    // Unsubscribe from existing listener if any
    const existingUnsubscribe = this.listeners.get(key);
    if (existingUnsubscribe) {
      existingUnsubscribe();
    }
    
    // Subscribe to course_assignments changes (more reliable with new trigger)
    const channel = supabase
      .channel(`course-progress-${key}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'course_assignments',
        filter: `user_id=eq.${userId}`
      }, async (payload) => {
        // Check if this update is for our course and it's completed
        if (payload.new && 
            (payload.new as any).course_id === courseId && 
            (payload.new as any).status === 'completed' &&
            (payload.new as any).final_grade === 100) {
          onComplete();
          toast.success('🎉 Course completed! Certificate being generated...', {
            description: 'Check your certificates page in a moment',
            duration: 5000
          });
        }
      })
      .subscribe();
      
    // Store unsubscribe function
    this.listeners.set(key, () => {
      channel.unsubscribe();
      this.listeners.delete(key);
    });
    
    return () => {
      const unsubscribe = this.listeners.get(key);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }
  
  static async checkCourseCompletion(userId: string, courseId: string): Promise<boolean> {
    try {
      const { data: assignment } = await supabase
        .from('course_assignments')
        .select('final_grade, status')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .single();
        
      return assignment?.final_grade === 100 && assignment?.status === 'completed';
    } catch (error) {
      console.error('Error checking course completion:', error);
      return false;
    }
  }
  
  static unwatchCourseProgress(userId: string, courseId: string) {
    const key = `${userId}-${courseId}`;
    const unsubscribe = this.listeners.get(key);
    if (unsubscribe) {
      unsubscribe();
    }
  }
  
  static unwatchAll() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }
}
